import { z } from 'zod'
import type { FirestoreSettingsSoundcheck } from '../../../firestore/SettingsSoundcheck/schema'

/**
 * Why the microphone is on or off right now. Supplied by the host (the room
 * adapter, the AV check, or the device picker). MicCheck never guesses intent
 * from a disabled track; it is told.
 */
export type MicContext =
  | 'active'
  | 'user_muted'
  // observers, and slides that isolate participants
  | 'not_required'
  | 'reconnecting'
  | 'repairing'

/** A confirmed microphone fault. */
export type MicFailure =
  | 'no_track'
  | 'track_stopped'
  | 'digital_silence'
  // A live track whose sound never rises above a faint hiss: it passes the
  // digital-zero test but nobody can hear it.
  | 'near_silence'

/** Whether the speech detector loaded and can report speech. */
export type SpeechDetection = 'available' | 'unavailable'

/**
 * What the microphone is doing. `ready` means a live, enabled track has produced
 * non-zero audio; it does not mean anyone else can hear it or that transcription
 * works.
 */
export type MicReport =
  | { state: 'inactive'; reason: 'user_muted' | 'not_required' }
  | {
      state: 'checking'
      reason: 'starting' | 'changing_device' | 'reconnecting' | 'repairing'
    }
  | {
      state: 'ready'
      hasHeardSpeech: boolean
      speechDetection: SpeechDetection
    }
  | { state: 'failed'; cause: MicFailure }

/** Which layer owns a problem. */
export type ProblemLayer = 'microphone' | 'delivery' | 'transcript'

/**
 * Everything the UI can be asked to explain. not_heard: the student's browser
 * heard them talk, and no server ever heard that mic.
 */
export type ProblemCause = MicFailure | 'not_heard' | 'no_transcript'

export type Problem = { cause: ProblemCause; since: number }

/**
 * How a resolved problem was recovered, for telemetry. fresh_start: a
 * transcript or delivery problem whose evidence was reset by a new or newly
 * ready mic, a restarted capture, or a restarted worker, rather than answered.
 * server_heard: a server heard the mic that the delivery problem was about.
 */
export type ResolvedBy =
  | 'mic_ready'
  | 'caption'
  | 'listening'
  | 'fresh_start'
  | 'server_heard'

/**
 * The Secretary heartbeat. The transcription worker sends it every 10s over the
 * LiveKit data channel as message type 16, without retries, so single messages
 * can be lost. It has one row per microphone the worker is transcribing. Only
 * the fields Soundcheck reads are listed; the worker sends more.
 */
export const secretaryStatusTrackSchema = z.object({
  identity: z.string(),
  // LiveKit microphone publication SID.
  trackId: z.string(),
  // Running total of times the worker's Silero speech detector started speech
  // on this mic. Absent from workers that do not count it.
  speechStarts: z.number().optional(),
})

export const secretaryStatusSchema = z.object({
  type: z.literal(16),
  // Unique to this worker run. Counters restart with each run.
  runId: z.string(),
  seq: z.number(),
  tracks: z.array(secretaryStatusTrackSchema),
})

export type SecretaryStatusTrack = z.infer<typeof secretaryStatusTrackSchema>
export type SecretaryStatus = z.infer<typeof secretaryStatusSchema>

/** The calibration the controller and MicCheck read: settings/soundcheck. */
export type SoundcheckConfig = FirestoreSettingsSoundcheck

/** Which host is running a MicCheck, for the soundcheck_mic_result telemetry. */
export type MicCheckHost = 'avcheck' | 'demo' | 'dialog' | 'meeting'
